import React from 'react';
import _ from 'lodash';

import {
  InventoryActivity_ItemTabQueryVariables,
  QueryItemsFilterInput,
  useExportItemListLazyQuery,
  useInventoryActivity_ItemTabLazyQuery
} from 'generated/graphql';
import {
  errorUtil,
  listQueryUtil,
  protectedFileUtil
} from 'iron-forge-react-modules';

import Big from 'big.js';
import * as types from './types';



const useListQuery = listQueryUtil.genUseListQuery<types.IColumnId>();

export interface IItem {
  id: string;
  name: string;
  sku: string;
  quantity: number;
  defaultPriceDollars: Big;
  defaultWholesalePriceDollars: Big;
  defaultCostDollars: Big;
  listingManagementStrategyCount: number;
  itemQtyLastModifiedDate: string;
  itemLastModifiedDate: string;
}

export interface IData {
  items: IItem[];
}

export function useQuery() {
  const useWrappedQuery = __genWrapQuery();

  return useListQuery({
    useLazyQuery: useWrappedQuery,
    genFilter2GqlFilter: () => (filter?: types.IFilter): QueryItemsFilterInput => ({
      hasListingManagementStrategyForStoreId: filter?.storeId
    }),
    defaultFilterArgs: {},
    clientColumn2GqlSortField: {
      name: 'name',
      sku: 'sku'
    },
    initQuerySort: {
      columnId: 'sku',
      direction: 'ascending'
    },

    gqlResultData2GqlPageInfo: gqlResultData =>
      gqlResultData?.items.pageInfo,

    gqlResultData2TupleData: (gqlTuple): IData => ({
      items: _.map(gqlTuple.data?.items.nodes ?? [], (item): IItem => ({
        id: item.id,
        name: item.name,
        sku: item.sku,
        quantity: item.currentQuantity,
        defaultPriceDollars: new Big(item.defaultPriceDollars),
        defaultCostDollars: new Big(item.defaultCostDollars),
        defaultWholesalePriceDollars: new Big(item.defaultWholesalePriceDollars),
        listingManagementStrategyCount: item.listingManagementStrategyCount,
        itemQtyLastModifiedDate: item.itemQtyLastModifiedDate,
        itemLastModifiedDate: item.itemLastModifiedDate
      }))
    })
  });
}



function __genWrapQuery() {
  function useWrappedQuery() {
    const [fnQuery, tuple] = errorUtil.useWrapLazyQueryOrMutationTuple(
      useInventoryActivity_ItemTabLazyQuery({
        notifyOnNetworkStatusChange: true
      }));

    const fnWrappedQuery = React.useCallback(
      (params: {
        variables: Pick<InventoryActivity_ItemTabQueryVariables, 'page' | 'filter' | 'sort'>;
      }) => {
        const variables = {
          ...params.variables
        };
        localStorage.setItem('InventoryActivityQueryVariables', JSON.stringify(variables));
        fnQuery({ variables });
      },
      [fnQuery]
    );

    return [
      fnWrappedQuery,
      tuple
    ] as const;
  }

  return useWrappedQuery;
}



export function useExportItemListQuery() {
  const [exportItemList, exportItemListTuple] = useExportItemListLazyQuery({});

  const fnWrappedQuery = React.useCallback(
    async (filter?: types.IFilter) => {
      const result = await exportItemList({
        variables: {
          options: {
            filter: {
              hasListingManagementStrategyForStoreId: filter?.storeId
            },
            sort: {
              direction: 'ascending',
              field: 'sku'
            }
          }
        }
      });

      if (result.data?.exportItemList) {
        protectedFileUtil.forceDownloadEncodedFile(result.data?.exportItemList);
      }
    },
    [exportItemList]
  );

  return [
    fnWrappedQuery,
    exportItemListTuple
  ] as const;
}
